import * as React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/Popover';
import { Label } from '../ui/Label';

// ----------------------------------------------------------------------

export interface RHFAutocompleteProps<T> {
  name: string;
  label?: string;
  helperText?: React.ReactNode;
  options: T[];
  getOptionLabel?: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  freeSolo?: boolean;
  disableClearable?: boolean;
}

export default function RHFAutocomplete<T>({
  name,
  label,
  helperText,
  options,
  getOptionLabel,
  isOptionEqualToValue,
  loading = false,
  disabled = false,
  placeholder = 'Select an option...',
  className,
  freeSolo: _freeSolo,
  disableClearable: _disableClearable
}: RHFAutocompleteProps<T>) {
  const { control } = useFormContext();
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const getLabel = React.useCallback(
    (option: T): string => {
      if (getOptionLabel) {
        return getOptionLabel(option);
      }
      if (typeof option === 'string') {
        return option;
      }
      if (option && typeof option === 'object' && 'label' in option) {
        return String((option as { label: string }).label);
      }
      return String(option);
    },
    [getOptionLabel]
  );

  const isEqual = React.useCallback(
    (option: T, value: T): boolean => {
      if (isOptionEqualToValue) {
        return isOptionEqualToValue(option, value);
      }
      return option === value;
    },
    [isOptionEqualToValue]
  );

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options;
    return options.filter((option) =>
      getLabel(option).toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery, getLabel]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div className={cn('w-full space-y-1', className)}>
          {label && (
            <Label htmlFor={name} className={cn(error && 'text-destructive')}>
              {label}
            </Label>
          )}

          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                disabled={disabled || loading}
                className={cn(
                  'w-full justify-between font-normal',
                  !field.value && 'text-muted-foreground',
                  error && 'border-destructive'
                )}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </span>
                ) : field.value ? (
                  getLabel(field.value)
                ) : (
                  placeholder
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <div className="flex flex-col">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-b border-border px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
                />
                <div className="max-h-60 overflow-y-auto">
                  {filteredOptions.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No options found.
                    </div>
                  ) : (
                    filteredOptions.map((option, index) => (
                      <div
                        key={index}
                        className={cn(
                          'relative flex cursor-pointer select-none items-center px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground',
                          field.value && isEqual(option, field.value) && 'bg-accent'
                        )}
                        onClick={() => {
                          field.onChange(option);
                          setOpen(false);
                          setSearchQuery('');
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            field.value && isEqual(option, field.value)
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        {getLabel(option)}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {(error || helperText) && (
            <p className={cn('text-sm', error ? 'text-destructive' : 'text-muted-foreground')}>
              {error ? error.message : helperText}
            </p>
          )}
        </div>
      )}
    />
  );
}
